import React from "react";
import {useTranslation} from "react-i18next";
import TabSection from "../../../../components/layout/TabSection.tsx";
import TabBox from "../../../../components/layout/TabBox.tsx";
import {ControlsSection} from "../../../../components/layout/ControlsSection.tsx";
import {BackButton} from "../../../../components/buttons/BackButton.tsx";
import StatusBadge from "../../../../components/StatusBadge.tsx";
import dateFormatter from "../../../../utils/DateFormatter.ts";
import {type CustomerView} from "../../../../model/crm/CustomerView.ts";
import {type CustomerCommunication} from "../../../../model/crm/CustomerCommunication.ts";
import CustomerMailHistory from "./customer/CustomerMailHistory.tsx";
import {AI_CUSTOMER_INTERESTS_ENABLED} from "../../../../utils/featureFlags.ts";

interface DeletedCustomerMainProps {
    customer: CustomerView;
    communications: CustomerCommunication[];
    communicationsLoading: boolean;
    sendCron?: string;
    navigateBackTo: string;
}

// Read-only view of a soft-deleted customer: master data as plain text, no
// vehicle purchases / campaigns, no save/edit/reactivate
const DeletedCustomerMain = (props: DeletedCustomerMainProps) => {
    const {t} = useTranslation();

    const customer = props.customer;
    const consent = customer.communicationChannels.consent;
    const interestsLabel = customer.interests
        .map(key => t(`product.productDetails.interests.${key}`))
        .join(", ");

    const field = (label: string, value: React.ReactNode) => (
        <div className="min-w-0">
            <div className="text-xs text-an-muted-2 tracking-wide">{label}</div>
            <div className="text-an-ink break-words">{value || "—"}</div>
        </div>
    );

    return (
        <div className="h-full">
            <div className="flex items-center gap-3 mb-6">
                <StatusBadge variant="error" label={t("crm.statusDeleted")}/>
                <span className="text-base text-an-ink">
                    {t("crm.deletedOn", {date: dateFormatter.formatDate(customer.deletedAt)})}
                </span>
            </div>
            <TabSection title={t("crm.title")}>
                <TabBox title={t("crm.prospectInformation")}>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-5">
                        {field(t("crm.salutation"), t(`crm.salutationOptions.${customer.salutation}`, customer.salutation))}
                        <div/>
                        {field(t("crm.firstName"), customer.firstName)}
                        {field(t("crm.lastName"), customer.lastName)}
                        <div className="col-span-2">
                            {field(t("crm.referenceNo"), customer.referenceNo)}
                        </div>
                        {field(t("crm.email"), customer.communicationChannels.email)}
                        {field(t("crm.phone"), customer.communicationChannels.phone)}
                        {field(t("crm.consent"), consent.accepted ? t("crm.consentGranted") : t("crm.consentNotGranted"))}
                        {field(t("crm.consentSource"), t(`crm.consentSourceOptions.${consent.source}`, consent.source))}
                        {AI_CUSTOMER_INTERESTS_ENABLED && (
                            <div className="col-span-2">
                                {field(t("crm.customerInterests"), interestsLabel)}
                            </div>
                        )}
                    </div>
                </TabBox>
                <CustomerMailHistory
                    communications={props.communications}
                    loading={props.communicationsLoading}
                    sendCron={props.sendCron}
                />
            </TabSection>
            <ControlsSection>
                <BackButton isFormDirty={false} navigateBackTo={props.navigateBackTo}/>
            </ControlsSection>
        </div>
    );
};

export default DeletedCustomerMain;
